/** @jsx jsx */

import { css, jsx } from '@emotion/core';

import {
  Checkbox,
  Select,
  SelectItem,
  Spacer,
  Text,
} from '@zapier/design-system';

import AsyncButton from 'app/developer-v3/components/AsyncButton';
import Link from 'app/common/components/Link';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import { useForm } from 'app/developer-v3/hooks';

import { getError } from './Steps/utils';

import type { SemVer } from 'app/developer-v3/types/version';

const Styles = {
  form: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  `,
};

type FormValues = {
  version?: SemVer;
  isConfirmed: boolean;
};

type Props = {
  isDisabled: boolean;
  onChangeVersion: (versionNext: SemVer) => void;
  onSubmit: (valuesNext: FormValues) => Promise<void>;
  versions: SemVer[];
};

export default function Submission({
  isDisabled,
  onChangeVersion,
  onSubmit,
  versions,
}: Props) {
  const form = useForm<FormValues>({
    enableReinitialize: true,
    initialValues: {
      version: '',
      isConfirmed: false,
    },
    onSubmit,
    validate: valuesNext => {
      const errorsNext: Partial<Record<keyof FormValues, string>> = {};

      if (!valuesNext.version) {
        errorsNext.version = 'Version is required.';
      }

      if (!valuesNext.isConfirmed) {
        errorsNext.isConfirmed = 'Confirmation is required.';
      }

      return errorsNext;
    },
  });

  return (
    <section>
      <Text margin="20px 0px" tag="h2" type="ParagraphHeader4">
        Ready for launch? We'll be in touch via email to start the process.
      </Text>
      <div css={Styles.form}>
        <Select
          errorMessage={getError('version', form.isTouched, form.errors)}
          isDisabled={isDisabled}
          isRequired={true}
          label="Version to publish"
          isInvalid={form.isTouched.version && !!form.errors.version}
          onBlur={() => form.setFieldTouched('version', true)}
          onChange={key => {
            const nextVersion = key as SemVer;
            form.setFieldValue('version', nextVersion);
            onChangeVersion(nextVersion);
          }}
          selectedKey={form.values.version || null}
        >
          {versions.map(v => (
            <SelectItem key={v} value={v}>
              {v}
            </SelectItem>
          ))}
        </Select>
        <Checkbox
          isDisabled={isDisabled}
          isSelected={form.values.isConfirmed}
          name="isConfirmed"
          onChange={() => {
            form.setFieldTouched('isConfirmed', true);
            form.setFieldValue('isConfirmed', !form.values.isConfirmed);
          }}
        >
          <div>
            I confirm all{' '}
            <Link
              href={`${BASE_DOC_URL_VB}/publish/integration-publishing-requirements`}
              target="_blank"
            >
              publishing requirements
            </Link>{' '}
            have been met.
          </div>
        </Checkbox>
      </div>
      <Spacer height={20} />
      <AsyncButton
        aria-label="Submit for review"
        isDisabled={
          isDisabled || !form.isDirty || !form.isValid || form.isSubmitting
        }
        isWorking={form.isSubmitting}
        onPress={() => form.onSubmit()}
      >
        Submit for review
      </AsyncButton>
    </section>
  );
}
